import {
  Create, Edit, EditButton, List, Show, SimpleForm, TextInput, Datagrid, FunctionField, required,
  useGetList, useRecordContext, useTranslate,
} from 'react-admin'
import { Box, Tab, Tabs } from '@mui/material'
import CityIcon from '@mui/icons-material/LocationCity'
import { FC, useState } from 'react'
import { CERTIFICATE_WRITE_SCOPE } from '../../config'
import { useHasScope } from '../../hooks/useHasScope'
import { useSettingsCrumbs } from '../../hooks/useSettingsCrumbs'
import { ConfirmDeleteToolbar, RestrictedButton, RestrictedCreateButton } from '../../components'
import { Card, EmptyState, FormSection, Muted, PageHeader, Property, PropertyDivider, formSurfaceSx, pageSx } from '../../components/ui'
import type { AuditLogOut, CertificateOut, MunicipalityOut } from '../../types/apiRequests'
import { AuditTimeline, CertificatesTable, RELATED_LIMIT, useLimitHint } from '../Certificates/CertificateRelated'

const MunicipalityList: FC = () => {
  const translate = useTranslate()
  const canWrite = useHasScope(CERTIFICATE_WRITE_SCOPE)
  const crumbs = useSettingsCrumbs('municipality')
  const createButton = <RestrictedCreateButton scope={CERTIFICATE_WRITE_SCOPE} />

  return (
    <Box sx={pageSx}>
      <PageHeader crumbs={crumbs} title={translate('resources.municipality.title')} actions={createButton} />
      {/* No search: the API only offers exact-match filters on this resource. */}
      <List
        actions={false}
        title={translate('resources.municipality.title')}
        empty={
          <EmptyState
            icon={<CityIcon />}
            title={translate('settings.municipality.emptyTitle')}
            text={translate(canWrite ? 'settings.municipality.emptyText' : 'settings.municipality.emptyTextReadOnly')}
            action={createButton}
          />
        }
      >
        <Datagrid rowClick="show" bulkActionButtons={false}>
          <FunctionField<MunicipalityOut>
            source="organisatienNaam"
            label="resources.municipality.fields.organisatienaam"
            className="col-main"
            cellClassName="col-main"
            render={(record) => (
              <>
                <Box sx={{ fontWeight: 600 }}>{record.organisatienNaam}</Box>
                <Box sx={{ fontSize: 12.5 }}>
                  <Muted>{[record.plaatsNaam, record.provincie].filter(Boolean).join(' · ')}</Muted>
                </Box>
              </>
            )}
          />
          <FunctionField<MunicipalityOut>
            source="contactNaam"
            label="resources.municipality.fields.contactpersoon"
            render={(record) => (
              <>
                <Box>{record.contactNaam}</Box>
                <Box sx={{ fontSize: 12.5 }}><Muted>{record.contactEmail}</Muted></Box>
              </>
            )}
          />
          <FunctionField<MunicipalityOut>
            source="contactTelefoon"
            label="data.phone"
            cellClassName="col-nowrap"
            render={(record) => record.contactTelefoon}
          />
        </Datagrid>
      </List>
    </Box>
  )
}

// Same fields for create and edit, in two settings-style sections.
const MunicipalityFields: FC = () => {
  const translate = useTranslate()
  return (
    <>
      <FormSection
        title={translate('settings.municipality.sectionMunicipality')}
        description={translate('settings.municipality.sectionMunicipalityHelp')}
      >
        <TextInput source="organisatienNaam" label="resources.municipality.fields.organisatienaam" validate={[required()]} fullWidth />
        <TextInput source="plaatsNaam" label="resources.municipality.fields.plaatsnaam" validate={[required()]} fullWidth />
        <TextInput source="provincie" label="resources.municipality.fields.provincie" validate={[required()]} fullWidth />
      </FormSection>
      <FormSection
        title={translate('settings.municipality.sectionContact')}
        description={translate('settings.municipality.sectionContactHelp')}
      >
        <TextInput source="contactNaam" label="resources.municipality.fields.contactpersoon" validate={[required()]} fullWidth />
        <TextInput source="contactTelefoon" label="data.phone" validate={[required()]} fullWidth />
        <TextInput source="contactEmail" type="email" label="data.email" validate={[required()]} fullWidth />
      </FormSection>
    </>
  )
}

const MunicipalityCreate: FC = () => {
  const translate = useTranslate()
  const crumbs = useSettingsCrumbs('municipality', null)
  return (
    <Create component="div" actions={false} sx={pageSx}>
      <PageHeader crumbs={crumbs} title={translate('resources.municipality.create')} />
      <Box sx={formSurfaceSx}>
        <SimpleForm>
          <MunicipalityFields />
        </SimpleForm>
      </Box>
    </Create>
  )
}

// Needs the record from the Edit context for the title.
const MunicipalityEditContent: FC = () => {
  const record = useRecordContext<MunicipalityOut>()
  const translate = useTranslate()
  const crumbs = useSettingsCrumbs('municipality', record?.organisatienNaam ?? translate('resources.municipality.edit'))
  return (
    <>
      <PageHeader crumbs={crumbs} title={record?.organisatienNaam ?? translate('resources.municipality.edit')} />
      <Box sx={formSurfaceSx}>
        <SimpleForm toolbar={<ConfirmDeleteToolbar access={[CERTIFICATE_WRITE_SCOPE]} />}>
          <MunicipalityFields />
        </SimpleForm>
      </Box>
    </>
  )
}

const MunicipalityEdit: FC = () => (
  <Edit component="div" actions={false} sx={pageSx} redirect="show">
    <MunicipalityEditContent />
  </Edit>
)

const dash = <Muted>—</Muted>

// Certificates of this municipality, newest first (API page size max is 100).
const useMunicipalityCertificates = (municipalityId: string | undefined) =>
  useGetList<CertificateOut>(
    'certificate',
    {
      filter: { municipalityId: [municipalityId] },
      sort: { field: 'created', order: 'DESC' },
      pagination: { page: 1, perPage: RELATED_LIMIT },
    },
    { enabled: !!municipalityId },
  )

const MunicipalityDetailsTab: FC<{ record: MunicipalityOut }> = ({ record }) => {
  const translate = useTranslate()
  return (
    <Card title={translate('settings.tabs.details')}>
      <Property label={translate('resources.municipality.fields.organisatienaam')}>{record.organisatienNaam}</Property>
      <Property label={translate('resources.municipality.fields.plaatsnaam')}>{record.plaatsNaam || dash}</Property>
      <Property label={translate('resources.municipality.fields.provincie')}>{record.provincie || dash}</Property>
      <PropertyDivider />
      <Property label={translate('resources.municipality.fields.contactpersoon')}>{record.contactNaam || dash}</Property>
      <Property label={translate('data.email')}>{record.contactEmail || dash}</Property>
      <Property label={translate('data.phone')}>{record.contactTelefoon || dash}</Property>
    </Card>
  )
}

const MunicipalityCertificatesTab: FC<{ record: MunicipalityOut }> = ({ record }) => {
  const translate = useTranslate()
  const { data, total, isLoading } = useMunicipalityCertificates(record.id)
  const hint = useLimitHint(total)
  return (
    <Card title={translate('settings.tabs.certificates')} subtitle={hint} flush>
      <CertificatesTable
        certificates={data}
        isLoading={isLoading}
        clickable
        emptyTitle={translate('resources.certificates.empty.title')}
        emptyText={translate('settings.municipality.noCertificatesText')}
      />
    </Card>
  )
}

// Audit logs of all certificates of the municipality; the certificates give the hostname per row.
const MunicipalityLogsTab: FC<{ record: MunicipalityOut }> = ({ record }) => {
  const translate = useTranslate()
  const certificates = useMunicipalityCertificates(record.id)
  const logs = useGetList<AuditLogOut>('auditlog', {
    filter: { municipalityId: record.id },
    sort: { field: 'created', order: 'DESC' },
    pagination: { page: 1, perPage: RELATED_LIMIT },
  })
  const hint = useLimitHint(logs.total)
  return (
    <Card title={translate('settings.tabs.logs')} subtitle={hint}>
      <AuditTimeline
        logs={logs.data}
        isLoading={logs.isLoading}
        error={logs.error}
        certificates={certificates.data}
        municipalities={[record]}
      />
    </Card>
  )
}

const MunicipalityShowContent: FC = () => {
  const record = useRecordContext<MunicipalityOut>()
  const translate = useTranslate()
  const [tab, setTab] = useState(0)
  const crumbs = useSettingsCrumbs('municipality', record?.organisatienNaam)
  if (!record) return null

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={record.organisatienNaam}
        subtitle={[record.plaatsNaam, record.provincie].filter(Boolean).join(' · ')}
        actions={(
          <RestrictedButton access={[CERTIFICATE_WRITE_SCOPE]} align="right">
            {(): JSX.Element => <EditButton variant="contained" />}
          </RestrictedButton>
        )}
      />
      <Tabs value={tab} onChange={(_, value: number) => setTab(value)}>
        <Tab label={translate('settings.tabs.details')} />
        <Tab label={translate('settings.tabs.certificates')} />
        <Tab label={translate('settings.tabs.logs')} />
      </Tabs>
      {tab === 0 && <MunicipalityDetailsTab record={record} />}
      {tab === 1 && <MunicipalityCertificatesTab record={record} />}
      {tab === 2 && <MunicipalityLogsTab record={record} />}
    </>
  )
}

const MunicipalityShow: FC = () => (
  <Show component="div" actions={false} sx={pageSx}>
    <MunicipalityShowContent />
  </Show>
)

export default {
  list: MunicipalityList,
  create: MunicipalityCreate,
  edit: MunicipalityEdit,
  show: MunicipalityShow,
  icon: CityIcon,
}
